import { useEffect, useMemo, useState } from "react";
import { useFormikContext, getIn } from "formik";
import { Button } from "primereact/button";
import { Chips } from "primereact/chips";
import { Column, ColumnProps } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import AbstractEditDialog from "../../pages/nomenclatures/common/AbstractEditDialog";
import { ApiResponse } from "../../services/base/apiResponse";

export type ItemBrowserProps<T> = {
  id: string;
  label?: string;
  options?: T[];
  optionsPromise?: () => any;
  loading?: boolean;
  idField: keyof T;
  labelField?: keyof T;
  currentLabelText?: string;
  onDialogClose?: () => void;
  onDialogSubmit?: (values: T | T[] | null) => void;
  columns?: ColumnProps[];
  buttonLabel?: string;
  createDialog?: any;
  createItemMutation?: any;
  header?: string;
};

const ItemBrowser = <T extends object>({
  id,
  label,
  options,
  optionsPromise,
  loading,
  idField,
  labelField,
  currentLabelText,
  onDialogClose,
  onDialogSubmit,
  columns,
  buttonLabel,
  createDialog,
  createItemMutation,
  header,
}: ItemBrowserProps<T>) => {
  const formik = useFormikContext<any>();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<T | T[] | null>(null);
  const [optionsState, setOptionsState] = useState<T[]>(options || []);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [createDialogState, setCreateDialogState] = useState<{
    open: boolean;
    item: T | null;
  }>({
    open: false,
    item: createDialog?.item ?? null,
  });

  const openCreateDialog = (item?: T | null) => {
    setCreateDialogState({ open: true, item: !item ? (createDialog.item ?? null) : item });
  };

  const closeCreateDialog = () => {
    setCreateDialogState({ open: false, item: null });
  };

  const handleCreateDialogSubmit = (values: ApiResponse<Partial<T>>) => {
    if (values?.data) {
      formik.setFieldValue(id, values?.data?.[idField]);
      onDialogSubmit?.(values?.data as T);
      setOptionsState([...optionsState, values?.data as T]);
      setIsVisible(false);
    }
  };

  useEffect(() => {
    if (!isVisible) return;

    const fetchOptions = async () => {
      if ((!options || options.length === 0) && optionsPromise) {
        const result = await optionsPromise().unwrap();
        setOptionsState(result.data);
      }
    };
    fetchOptions();
  }, [isVisible]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return optionsState;
    return optionsState.filter((option) =>
      Object.values(option).some((value) => String(value).toLowerCase().includes(searchQuery.toLowerCase())),
    );
  }, [searchQuery, optionsState]);

  const selectedOptions = useMemo(() => {
    const formikValue = getIn(formik.values, id);
    const isValueArray = Array.isArray(formikValue);
    const result = optionsState.filter((x) =>
      isValueArray ? formikValue.includes(x[idField]) : formikValue === x[idField],
    );
    return Array.isArray(formikValue) ? result : result[0];
  }, [formik.values, id, optionsState, idField]);

  useEffect(() => {
    setSelectedItems(selectedOptions);
  }, [selectedOptions]);

  const handleSubmit = (_: any) => {
    formik.setFieldValue(
      id,
      Array.isArray(selectedItems) ? selectedItems.map((x) => x[idField]) : selectedItems?.[idField],
    );
    onDialogSubmit?.(selectedItems);
    setIsVisible(false);
  };

  const handleHide = () => {
    setIsVisible(false);
    onDialogClose?.();
  };

  const cols = useMemo(
    () =>
      columns
        ? columns.map((column) => <Column key={column.field} {...column} />)
        : Object.keys(optionsState[0] || {}).map((key) => (
            <Column key={key} field={key} filter header={key.charAt(0).toUpperCase() + key.slice(1)} />
          )),
    [optionsState],
  );

  return (
    <>
      <div className="flex flex-column gap-2 mb-2">
        <label>{label}</label>
        <div className="flex flex-row gap-2 w-full">
          <Chips
            id={id}
            value={
              Array.isArray(selectedOptions)
                ? selectedOptions.map((x) => x[labelField || idField] as string)
                : selectedOptions
                  ? [selectedOptions?.[labelField || idField] as string]
                  : currentLabelText
                    ? [currentLabelText]
                    : []
            }
            readOnly
            className="w-full"
            pt={{
              container: { className: 'w-full' },
            }}
          />
          <Button type="button" icon="pi pi-ellipsis-h" onClick={() => setIsVisible((state) => !state)}></Button>
        </div>
      </div>
      <Dialog
        visible={isVisible}
        onHide={handleHide}
        closeOnEscape
        className="max-w-screen"
        header={`Изберете ${label}`}
        footer={
          <>
            <Button
              type="button"
              onClick={handleSubmit}
              label="Избери"
              severity="info"
              className="font-bold"
              disabled={selectedItems === null || (Array.isArray(selectedItems) && selectedItems.length === 0)}
            />
            <Button type="button" onClick={handleHide} label="Отказ" severity="warning" />
          </>
        }
      >
        {loading && <ProgressSpinner />}
        {!loading && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <InputText
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Търсене..."
              />
              {buttonLabel && <Button type="button" label={buttonLabel} onClick={() => openCreateDialog(null)} />}
            </div>
            {Array.isArray(getIn(formik.values, id)) ? (
              <DataTable
                value={filteredOptions}
                scrollable
                selectionMode="checkbox"
                selection={selectedItems as T[]}
                onSelectionChange={(e: any) => setSelectedItems(e.value)}
                paginator
                rows={50}
                rowsPerPageOptions={[10, 25, 50]}
                showGridlines
                stripedRows
              >
                <Column selectionMode="multiple" headerStyle={{ width: "3rem" }}></Column>
                {cols}
              </DataTable>
            ) : (
              <DataTable
                value={filteredOptions}
                scrollable
                selectionMode="radiobutton"
                selection={selectedItems}
                onSelectionChange={(e: any) => setSelectedItems(e.value)}
                paginator
                rows={50}
                rowsPerPageOptions={[10, 25, 50]}
                showGridlines
                stripedRows
              >
                <Column selectionMode="single" headerStyle={{ width: "3rem" }}></Column>
                {cols}
              </DataTable>
            )}
          </>
        )}
      </Dialog>

      {createDialogState.open && (
        <AbstractEditDialog
          item={createDialogState.item}
          onDialogClose={closeCreateDialog}
          onDialogSubmit={handleCreateDialogSubmit}
          updateMutation={createItemMutation}
          validationSchema={createDialog.validationSchema}
          renderForm={(formik) => createDialog.renderForm(formik)}
          header={header}
        />
      )}
    </>
  );
};

export default ItemBrowser;
